import React from "react";
import { TourSearchResult } from "@/types/search/tour-search-results";
import { Box, Card, Rating, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ColletteButton, ColletteChip } from "@collette-travel/collette-common-controls";
import TourCardPricing from "@/components/common/tour-card/tour-card-pricing";
import { CalendarTodayOutlined } from "@mui/icons-material";
import ActivityLevelMeterIcon from "@/components/common/activity-level-indicator/activity-level-meter-icon";
import Link from "next/link";

interface TourCardProps extends TourSearchResult {
  openRequestAQuote: () => void;
}

const TourCard = ({
  tourId,
  title,
  subTitle,
  searchImage,
  badges,
  tourStyles,
  feefoStars,
  feefoReviewCount,
  price,
  crossedOutPrice,
  offerSavings,
  numberDays,
  activityLevel,
  activityLevelLabel,
  highlights,
  extensions,
  tourUrl,
  comingSoon = true,
  openRequestAQuote
}: TourCardProps) => {
  const t = useTranslations("SEARCH");

  const testId = `${tourId}_${subTitle ? "alt" : ""}`;

  if (comingSoon) {
    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          boxShadow: 6,
          borderRadius: "2rem",
          backgroundColor: "background.default",
          overflow: "hidden",
          position: "relative",
          height: "100%",
          width: "100%",
          minHeight: "54rem",
          maxWidth: "38.6rem",
          minWidth: "30rem"
        }}
      >
        <Image
          src={searchImage.imageUrl || ""}
          alt={searchImage.altText || ""}
          height={204}
          width={306}
          style={{
            height: "100%",
            width: "100%",
            objectFit: "cover",
            objectPosition: "center",
            overflow: "hidden",
            backgroundColor: "var(--theme-palette-background-warm_strong)"
          }}
          data-testid={`${tourId}_coming-soon-tour-card_hero-image`}
        />
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            opacity: 0.5,
            backgroundColor: "common.black",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            alignItems: "center"
          }}
        />
        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingX: "2.4rem",
            gap: "2.4rem",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0
          }}
        >
          <Link
            href={tourUrl}
            tabIndex={-1}
            style={{
              zIndex: 5,
              alignItems: "center",
              display: "flex"
            }}
            className="pseudo-absolute-full-container"
           />
          <Typography
            variant="subtitle_lg"
            sx={{
              color: "common.white",
              textAlign: "center",
              position: "absolute",
              top: "3rem",
              width: "100%"
            }}
          >
            {t("COMING SOON")}
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "common.white" }}
            data-testid={`${testId}_coming-soon-tour-card_title`}
          >
            {title} {subTitle}
          </Typography>
          <ColletteButton
            component="a"
            color="secondary"
            size="large"
            sx={{
              maxWidth: "15rem",
              whiteSpace: "nowrap",
              zIndex: 6
            }}
            label={t("Preview Tour")}
            testId={`${testId}_coming-soon-tour-card_CTA-button`}
            href={tourUrl}
          />
        </Stack>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        boxShadow: 6,
        borderRadius: "2rem",
        backgroundColor: "background.default",
        maxWidth: "38.6rem",
        minWidth: "30rem",
        overflow: "hidden",
        position: "relative",
        height: "100%"
      }}
    >
      {/*  Image  */}
      <Box
        component="a"
        href={tourUrl}
        tabIndex={-1}
        data-testid={`${testId}_tour-card_full-card-link`}
        className="pseudo-absolute-full-container"
      >
        <Image
          src={searchImage.imageUrl || ""}
          alt={searchImage.altText || ""}
          height={204}
          width={306}
          style={{
            width: "100%",
            objectFit: "cover",
            objectPosition: "center",
            overflow: "hidden",
            backgroundColor: "var(--theme-palette-background-warm_strong)"
          }}
          data-testid={`${testId}_tour-card_hero-image`}
        />
      </Box>
      {/* Badges */}
      <Stack
        sx={{
          position: "absolute",
          top: "1.6rem",
          right: "1.6rem",
          gap: "1rem",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "flex-end"
        }}
        data-testid={`${testId}_tour-card_badges`}
      >
        {badges.map((badge) => (
          <ColletteChip
            key={badge.label}
            label={badge.label}
            color={badge.color}
            sx={{
              borderRadius: "0.8rem",
              fontWeight: 600,
              fontSize: "1.2rem"
            }}
            testId={`${testId}_tour-card_badge_${badge.label}`}
          />
        ))}
      </Stack>

      <Stack
        sx={{
          padding: "1.6rem",
          gap: "1.6rem"
        }}
      >
        {/*  Title  */}
        <Typography
          sx={{
            fontSize: "1.6rem",
            fontWeight: 600,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            WebkitLineClamp: 3,
            textOverflow: "ellipsis",
            "@container tour-search-results-grid (min-width: 63.2rem)": {
              minHeight: "3lh"
            }
          }}
          data-testid={`${testId}_tour-card_title`}
        >
          {title} {subTitle}
        </Typography>

        {/*  Travel Styles  */}
        <Stack gap="1.6rem" direction="row">
          {tourStyles.map((style) => (
            <ColletteChip
              key={style}
              color="info"
              testId={`${testId}_tour-card_travel-styles_${style}`}
              sx={{
                borderRadius: "0.8rem",
                fontWeight: 600,
                fontSize: "1.2rem"
              }}
              label={style}
            />
          ))}
        </Stack>

        {/*  Reviews  */}
        <Stack
          gap="1.2rem"
          direction="row"
          alignItems="center"
          sx={{
            height: "1.8rem",
            width: "100%",
            "@container tour-search-results-grid (max-width: 63.2rem)": {
              display: feefoReviewCount === 0 ? "block" : "none"
            }
          }}
        >
          {feefoReviewCount > 0 && (
            <>
              <Rating
                value={feefoStars}
                readOnly
                data-testid={`${testId}_tour-card_rating`}
                precision={0.1}
                size="small"
              />
              <Typography fontSize="1.2rem" data-testid={`${testId}_tour-card_total-ratings`}>
                {t("reviews", { count: feefoReviewCount })}
              </Typography>
            </>
          )}
        </Stack>

        {/*  Pricing  */}
        <TourCardPricing
          tourId={tourId}
          price={price}
          crossedOutPrice={crossedOutPrice}
          offerSavings={offerSavings}
        />

        {/*  Days and Activity Level  */}
        <Stack gap="0.8rem" justifyContent="space-between" direction="row" alignItems="center">
          <Stack flexDirection="row" gap="0.8rem" alignItems="center" whiteSpace="nowrap">
            <CalendarTodayOutlined
              sx={{ height: 18, width: 18 }}
              data-testid={`${testId}_tour-card_calendar-icon`}
            />
            <Typography
              fontSize="1.2rem"
              data-testid={`${testId}_tour-card_number-of-days`}
              whiteSpace="nowrap"
            >
              {numberDays}
            </Typography>
          </Stack>
          <Stack flexDirection="row" gap="0.8rem" alignItems="center" whiteSpace="nowrap">
            <ActivityLevelMeterIcon activityLevel={activityLevel} />
            <Typography
              fontSize="1.2rem"
              data-testid={`${testId}_tour-card_activity-level`}
              whiteSpace="nowrap"
            >
              {activityLevelLabel}
            </Typography>
          </Stack>
        </Stack>

        {/*  Tags  */}
        <Typography
          sx={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            WebkitLineClamp: 3,
            textOverflow: "ellipsis",
            fontSize: "1.2rem",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "normal",
            textWrap: "balance",
            minHeight: "3lh"
          }}
          data-testid={`${testId}_tour-card_highlights`}
          dangerouslySetInnerHTML={{ __html: highlights.join(" • ") }}
        />

        {/*  Optional Extensions  */}
        <Box
          sx={{
            height: "7rem",
            width: "100%",
            "@container tour-search-results-grid (max-width: 63.2rem)": {
              display: extensions ? "block" : "none"
            }
          }}
          height="7rem"
          width="100%"
        >
          {extensions && (
            <Box
              sx={{
                display: "flex",
                padding: "1rem",
                borderRadius: "1rem",
                backgroundColor: "background.cool_strong"
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  minHeight: "3lh",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  WebkitLineClamp: 3,
                  textOverflow: "ellipsis"
                }}
                data-testid={`${testId}_tour-card_extensions`}
              >
                {`${t("This tour has optional extensions")} - ${extensions.join(", ")}`}
              </Typography>
            </Box>
          )}
        </Box>
        {/*  Buttons */}
        <Stack sx={{ position: "relative", flexDirection: "row", gap: "1.2rem" }}>
          <ColletteButton
            variant="outlined"
            size="large"
            label={t("Get a Quote")}
            testId={`${testId}_tour-card_get-a-quote-button`}
            sx={{
              whiteSpace: "nowrap",
              width: "50%",
              zIndex: 4
            }}
            onClick={openRequestAQuote}
          />
          <ColletteButton
            component="a"
            color="secondary"
            size="large"
            label={t("View Tour")}
            testId={`${testId}_tour-card_view-tour-button`}
            href={tourUrl}
            sx={{
              whiteSpace: "nowrap",
              width: "50%"
            }}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

export default TourCard;
